import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { isNil } from 'ramda';
import type { Preset } from '../../../types/Api';
const PRESET_WRAPPER_HEIGHT = 36;
type UsePresetOverflowProps = {
  presets: Preset[] | undefined;
};
type UsePresetOverflowResult = {
  visibleCount: number | undefined;
  wrapperWidth: number;
  buttonsWrapperRef: RefObject<HTMLDivElement | null>;
  buttonsGhostRef: RefObject<HTMLDivElement | null>;
};

/**
 * Measures preset button overflow using a ResizeObserver on a ghost div.
 *
 * The ghost div mirrors the visible preset buttons at full width but is hidden
 * from users. When its size changes the observer recalculates how many buttons
 * fit within PRESET_WRAPPER_HEIGHT and updates visibleCount accordingly.
 */
export function usePresetOverflow(_props) {
  const $ = _c(6);
  const buttonsWrapperRef = useRef(null);
  const buttonsGhostRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState();
  const [wrapperWidth, setWrapperWidth] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ghostParent => {
      if (isNil(ghostParent)) {
        return;
      }
      let overflowDetected = false;
      for (const [index, child] of (Array.from(ghostParent.children) as HTMLElement[]).entries()) {
        const childBottom = child.offsetTop + child.offsetHeight;
        if (childBottom > PRESET_WRAPPER_HEIGHT) {
          setVisibleCount(index);
          overflowDetected = true;
          break;
        }
      }
      if (!overflowDetected) {
        setVisibleCount(ghostParent.children.length);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const calculateVisibleItemCount = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const wrapperObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          if (buttonsGhostRef.current && buttonsWrapperRef.current) {
            const width = buttonsWrapperRef.current.offsetWidth;
            buttonsGhostRef.current.style.width = `${width}px`;
            setWrapperWidth(width);
          }
        });
      });
      if (buttonsWrapperRef.current) {
        wrapperObserver.observe(buttonsWrapperRef.current);
      }
      const ghostObserver = new ResizeObserver(entries => {
        window.requestAnimationFrame(() => {
          calculateVisibleItemCount(entries[0].target as HTMLElement);
        });
      });
      if (buttonsGhostRef.current) {
        ghostObserver.observe(buttonsGhostRef.current);
      }
      return () => {
        ghostObserver.disconnect();
        wrapperObserver.disconnect();
      };
    };
    t2 = [calculateVisibleItemCount];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== visibleCount || $[4] !== wrapperWidth) {
    t3 = {
      visibleCount,
      wrapperWidth,
      buttonsWrapperRef,
      buttonsGhostRef
    };
    $[3] = visibleCount;
    $[4] = wrapperWidth;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}